import { ScanProvider } from '@hakimo-ui/hakimo/providers';
import { WsConnectionStatus } from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import {
  useAuthUtils,
  useScanWs,
  useUser,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import { ReactNode, useMemo } from 'react';

import {
  DuplicateSession,
  LoadingIndicator,
} from '@hakimo-ui/hakimo/ui-elements';
import ScanMonitoring from './pages/scan-monitoring/ScanMonitoring';

export function ScanRoot() {
  const user = useUser();
  const { getAccessToken } = useAuthUtils();

  const wsUrl = useMemo(() => {
    const host = window.location.hostname;
    if (host === 'localhost') {
      const port = window.location.port;
      return `ws://${host}:${port}/events`;
    }
    return `wss://event-flow-${host}/events`;
  }, []);

  return (
    <Page title="Scan" titleFontSize="large">
      {user.visionTenants && user.visionTenants.length > 0 && wsUrl ? (
        <ScanProvider url={wsUrl} getAccessToken={getAccessToken}>
          <ScanWrapper>
            <ScanMonitoring visionTenants={user.visionTenants} />
          </ScanWrapper>
        </ScanProvider>
      ) : (
        <Alert type="info">
          You do not currently have access to this page. Please contact the
          system administrator to request access permissions. Thank you!
        </Alert>
      )}
    </Page>
  );
}

interface ScanWrapperProps {
  children: ReactNode;
}
function ScanWrapper(props: ScanWrapperProps) {
  const {
    connectionState: { status, isDuplicateSession, nextRetryIn },
    reconnect,
  } = useScanWs();

  switch (status) {
    case WsConnectionStatus.PENDING:
      return (
        <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg z-10 flex h-full w-full items-center justify-center">
          <LoadingIndicator text="Connecting..." />
        </div>
      );
    case WsConnectionStatus.CLOSED:
      if (isDuplicateSession) {
        return <DuplicateSession title="Scan" reconnect={reconnect} />;
      }
      return (
        <Alert type="error">
          Scan connection terminated. This may be due to a connection error or
          login from another location. Refresh to use Scan in this window.
        </Alert>
      );
    case WsConnectionStatus.ERROR:
      return (
        <Alert type="error">
          An error occurred while connecting to Scan. Please try again later.
        </Alert>
      );
    case WsConnectionStatus.RECONNECTING:
    case WsConnectionStatus.CONNECTED:
      return (
        <>
          {status === WsConnectionStatus.RECONNECTING && (
            <Alert type="info">
              Unable to connect. Trying again in {nextRetryIn} seconds...
            </Alert>
          )}
          {props.children}
        </>
      );
      return;

    default:
      return null;
  }
}

export default withErrorBoundary(ScanRoot);
